/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable consistent-return */

import { Chart } from 'chart.js/auto';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useChartJsIntersectionObserver from '../../../../hooks/intersectionObserver/charts/useChartJsIntersectionObserver';
import formatDoughnutDataset from '../helpers/formatDoughnutDataset';
import type DoughnutDataset from '../types/DoughnutDataset';

const useDoughnut = (canvas: any, datasets: DoughnutDataset[]) => {
    const chartRef = useRef<any>(undefined);

    const chartData = useMemo(() => formatDoughnutDataset(datasets), [datasets]);

    const render = useCallback(() => {
        if (!canvas.current) return;
        if (chartRef.current) return;

        chartRef.current = new Chart(canvas.current, {
            type: 'doughnut',
            data: chartData,
            options: {
                cutout: '88%', // The space inside the doughnut
                plugins: {
                    tooltip: {
                        enabled: false,
                    },
                    legend: {
                        display: false,
                    },
                },
                elements: {
                    arc: {
                        // Border around the arc, can be useful for an arc that is supposed to be empty
                        // (see bottom doughnut charts on desktop design).
                        borderWidth: 0,
                    },
                },
                animation: {
                    delay: 500,
                },
            },
        });
    }, [canvas, chartData]);

    const { hasRenderedRef } = useChartJsIntersectionObserver({
        chartRef: canvas,
        animationDuration: 1000,
        threshold: 0.3,
        render,
    });

    /** render will change if the data changes */
    useEffect(() => {
        if (hasRenderedRef.current) {
            chartRef.current?.destroy();
            render();
        }
    }, [render, hasRenderedRef]);

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            chartRef.current?.destroy();
            chartRef.current = null;
        };
    }, [chartData, canvas, chartRef]);

    return { chartRef };
};

export default useDoughnut;
